// String Constants for SwipeSage - Ready for Localization
// All hardcoded strings should be moved here for easy localization

export const APP_STRINGS = {
  // App Information
  APP_NAME: 'SwipeSage',
  APP_DESCRIPTION: 'AI-powered credit card recommendation platform',
  APP_TAGLINE: 'Find your perfect credit card match',
  APP_VERSION: '1.0.0',
  
  // Company Information
  COMPANY_NAME: 'SwipeSage Technologies',
  COMPANY_WEBSITE: 'https://swipesage.com',
  SUPPORT_EMAIL: 'support@swipesage.com',
  PRIVACY_POLICY_URL: '/privacy',
  TERMS_OF_SERVICE_URL: '/terms',
  
  // Copyright
  COPYRIGHT_TEXT: '© 2024 SwipeSage. All rights reserved.',
  
  // Common Actions
  ACTIONS: {
    SAVE: 'Save',
    CANCEL: 'Cancel',
    DELETE: 'Delete',
    EDIT: 'Edit',
    ADD: 'Add',
    CREATE: 'Create',
    UPDATE: 'Update',
    SUBMIT: 'Submit',
    CONFIRM: 'Confirm',
    BACK: 'Back',
    NEXT: 'Next',
    PREVIOUS: 'Previous',
    CLOSE: 'Close',
    OPEN: 'Open',
    SEARCH: 'Search',
    FILTER: 'Filter',
    SORT: 'Sort',
    EXPORT: 'Export',
    IMPORT: 'Import',
    DOWNLOAD: 'Download',
    UPLOAD: 'Upload',
    VIEW: 'View',
    HIDE: 'Hide',
    SHOW: 'Show',
    ENABLE: 'Enable',
    DISABLE: 'Disable',
    ACTIVATE: 'Activate',
    DEACTIVATE: 'Deactivate',
    APPROVE: 'Approve',
    REJECT: 'Reject',
    PUBLISH: 'Publish',
    UNPUBLISH: 'Unpublish',
    ARCHIVE: 'Archive',
    RESTORE: 'Restore',
    DUPLICATE: 'Duplicate',
    COPY: 'Copy',
    PASTE: 'Paste',
    CUT: 'Cut',
    UNDO: 'Undo',
    REDO: 'Redo',
    REFRESH: 'Refresh',
    RELOAD: 'Reload',
    RETRY: 'Retry',
    RESET: 'Reset',
    CLEAR: 'Clear',
    REMOVE: 'Remove',
    SELECT: 'Select',
    CHOOSE: 'Choose',
    PICK: 'Pick',
    BROWSE: 'Browse',
    UPLOAD_FILE: 'Upload File',
    CHOOSE_FILE: 'Choose File',
    DRAG_DROP: 'Drag and drop files here',
    CLICK_UPLOAD: 'Click to upload',
    MAX_FILE_SIZE: 'Max file size: {size}',
    SUPPORTED_FORMATS: 'Supported formats: {formats}',
  },
  
  // Form Labels and Placeholders
  FORMS: {
    // Personal Information
    FIRST_NAME: 'First Name',
    LAST_NAME: 'Last Name',
    FULL_NAME: 'Full Name',
    EMAIL: 'Email Address',
    PHONE: 'Phone Number',
    MOBILE: 'Mobile Number',
    DATE_OF_BIRTH: 'Date of Birth',
    GENDER: 'Gender',
    AGE: 'Age',
    
    // Address
    ADDRESS: 'Address',
    STREET: 'Street Address',
    CITY: 'City',
    STATE: 'State',
    COUNTRY: 'Country',
    PINCODE: 'PIN Code',
    POSTAL_CODE: 'Postal Code',
    ZIP_CODE: 'ZIP Code',
    
    // Employment
    EMPLOYMENT: 'Employment',
    EMPLOYER: 'Employer',
    COMPANY: 'Company',
    DESIGNATION: 'Designation',
    JOB_TITLE: 'Job Title',
    ANNUAL_INCOME: 'Annual Income',
    MONTHLY_INCOME: 'Monthly Income',
    EMPLOYMENT_TYPE: 'Employment Type',
    
    // Financial
    CREDIT_SCORE: 'Credit Score',
    BANK_ACCOUNT: 'Bank Account',
    ACCOUNT_NUMBER: 'Account Number',
    IFSC_CODE: 'IFSC Code',
    BANK_NAME: 'Bank Name',
    BRANCH: 'Branch',
    
    // Documents
    PAN_CARD: 'PAN Card',
    AADHAR_CARD: 'Aadhar Card',
    DRIVING_LICENSE: 'Driving License',
    PASSPORT: 'Passport',
    VOTER_ID: 'Voter ID',
    INCOME_PROOF: 'Income Proof',
    ADDRESS_PROOF: 'Address Proof',
    BANK_STATEMENT: 'Bank Statement',
    SALARY_SLIP: 'Salary Slip',
    
    // Authentication
    PASSWORD: 'Password',
    CONFIRM_PASSWORD: 'Confirm Password',
    CURRENT_PASSWORD: 'Current Password',
    NEW_PASSWORD: 'New Password',
    FORGOT_PASSWORD: 'Forgot Password?',
    REMEMBER_ME: 'Remember Me',
    SIGN_IN: 'Sign In',
    SIGN_OUT: 'Sign Out',
    SIGN_UP: 'Sign Up',
    LOGIN: 'Login',
    LOGOUT: 'Logout',
    REGISTER: 'Register',
    RESET_PASSWORD: 'Reset Password',
    CHANGE_PASSWORD: 'Change Password',
    
    // Placeholders
    PLACEHOLDERS: {
      ENTER_EMAIL: 'Enter your email address',
      ENTER_PASSWORD: 'Enter your password',
      ENTER_NAME: 'Enter your name',
      ENTER_PHONE: 'Enter your phone number',
      ENTER_ADDRESS: 'Enter your address',
      ENTER_CITY: 'Enter your city',
      ENTER_STATE: 'Enter your state',
      ENTER_PINCODE: 'Enter PIN code',
      ENTER_INCOME: 'Enter your annual income',
      ENTER_EMPLOYER: 'Enter your employer name',
      ENTER_DESIGNATION: 'Enter your designation',
      ENTER_CREDIT_SCORE: 'Enter your credit score',
      SELECT_GENDER: 'Select your gender',
      SELECT_EMPLOYMENT_TYPE: 'Select employment type',
      SELECT_CITY: 'Select your city',
      SELECT_STATE: 'Select your state',
      SELECT_COUNTRY: 'Select your country',
      UPLOAD_DOCUMENT: 'Upload document',
      SEARCH_CARDS: 'Search credit cards...',
      SEARCH_USERS: 'Search users...',
      SEARCH_APPLICATIONS: 'Search applications...',
    },
  },
  
  // Validation Messages
  VALIDATION: {
    REQUIRED: '{field} is required',
    INVALID_EMAIL: 'Please enter a valid email address',
    INVALID_PHONE: 'Please enter a valid phone number',
    INVALID_PINCODE: 'Please enter a valid PIN code',
    INVALID_PAN: 'Please enter a valid PAN number',
    INVALID_AADHAR: 'Please enter a valid Aadhar number',
    PASSWORD_MIN_LENGTH: 'Password must be at least {min} characters',
    PASSWORD_MISMATCH: 'Passwords do not match',
    INVALID_CREDIT_SCORE: 'Credit score must be between 300 and 900',
    INVALID_INCOME: 'Please enter a valid income amount',
    INVALID_AGE: 'Age must be between {min} and {max} years',
    FILE_TOO_LARGE: 'File size must be less than {size}',
    INVALID_FILE_TYPE: 'Please upload a valid file type',
    ALREADY_EXISTS: '{item} already exists',
    NOT_FOUND: '{item} not found',
    UNAUTHORIZED: 'You are not authorized to perform this action',
    FORBIDDEN: 'Access denied',
    SERVER_ERROR: 'Something went wrong. Please try again.',
    NETWORK_ERROR: 'Network error. Please check your connection.',
    TIMEOUT_ERROR: 'Request timed out. Please try again.',
  },
  
  // Success Messages
  SUCCESS: {
    SAVED: '{item} saved successfully',
    UPDATED: '{item} updated successfully',
    DELETED: '{item} deleted successfully',
    CREATED: '{item} created successfully',
    UPLOADED: 'File uploaded successfully',
    DOWNLOADED: 'File downloaded successfully',
    EXPORTED: 'Data exported successfully',
    IMPORTED: 'Data imported successfully',
    PUBLISHED: '{item} published successfully',
    APPROVED: '{item} approved successfully',
    REJECTED: '{item} rejected successfully',
    ACTIVATED: '{item} activated successfully',
    DEACTIVATED: '{item} deactivated successfully',
    LOGGED_IN: 'Successfully signed in',
    LOGGED_OUT: 'Successfully signed out',
    PASSWORD_CHANGED: 'Password changed successfully',
    PASSWORD_RESET: 'Password reset email sent',
    PROFILE_UPDATED: 'Profile updated successfully',
    SETTINGS_SAVED: 'Settings saved successfully',
    NOTIFICATION_SENT: 'Notification sent successfully',
    REFERRAL_SENT: 'Referral link sent successfully',
    APPLICATION_SUBMITTED: 'Application submitted successfully',
    CARD_ADDED: 'Credit card added successfully',
    CARD_UPDATED: 'Credit card updated successfully',
    CARD_DELETED: 'Credit card deleted successfully',
    USER_CREATED: 'User created successfully',
    USER_UPDATED: 'User updated successfully',
    USER_DELETED: 'User deleted successfully',
  },
  
  // Error Messages
  ERRORS: {
    // Authentication Errors
    INVALID_CREDENTIALS: 'Invalid email or password',
    USER_NOT_FOUND: 'No account found with this email address',
    ACCOUNT_DISABLED: 'This account has been disabled',
    TOO_MANY_ATTEMPTS: 'Too many failed attempts. Please try again later',
    SESSION_EXPIRED: 'Your session has expired. Please sign in again',
    ACCESS_DENIED: 'Access denied. Admin privileges required.',
    
    // Network Errors
    NETWORK_UNAVAILABLE: 'Network is unavailable',
    REQUEST_FAILED: 'Request failed. Please try again',
    UPLOAD_FAILED: 'File upload failed. Please try again',
    DOWNLOAD_FAILED: 'File download failed. Please try again',
    
    // Database Errors
    DATA_NOT_FOUND: 'Data not found',
    DUPLICATE_ENTRY: 'This entry already exists',
    CONSTRAINT_VIOLATION: 'Operation failed due to data constraints',
    
    // File Errors
    FILE_NOT_FOUND: 'File not found',
    FILE_CORRUPTED: 'File is corrupted',
    INSUFFICIENT_PERMISSIONS: 'Insufficient permissions to access this file',
    
    // General Errors
    UNKNOWN_ERROR: 'An unknown error occurred',
    OPERATION_FAILED: 'Operation failed',
    INVALID_OPERATION: 'Invalid operation',
    RESOURCE_BUSY: 'Resource is busy. Please try again later',
    QUOTA_EXCEEDED: 'Quota exceeded. Please try again later',
  },
  
  // Status Messages
  STATUS: {
    LOADING: 'Loading...',
    SAVING: 'Saving...',
    UPLOADING: 'Uploading...',
    DOWNLOADING: 'Downloading...',
    PROCESSING: 'Processing...',
    SEARCHING: 'Searching...',
    CONNECTING: 'Connecting...',
    SYNCING: 'Syncing...',
    UPDATING: 'Updating...',
    DELETING: 'Deleting...',
    PUBLISHING: 'Publishing...',
    APPROVING: 'Approving...',
    REJECTING: 'Rejecting...',
    ACTIVATING: 'Activating...',
    DEACTIVATING: 'Deactivating...',
    SEEDING_DATA: 'Seeding sample data...',
  },
  
  // Navigation
  NAVIGATION: {
    DASHBOARD: 'Dashboard',
    CREDIT_CARDS: 'Credit Cards',
    USERS: 'Users',
    APPLICATIONS: 'Applications',
    ANALYTICS: 'Analytics',
    SETTINGS: 'Settings',
    PROFILE: 'Profile',
    HELP: 'Help',
    ABOUT: 'About',
    CONTACT: 'Contact',
    PRIVACY: 'Privacy Policy',
    TERMS: 'Terms of Service',
    FAQ: 'FAQ',
    SUPPORT: 'Support',
    LOGOUT: 'Logout',
    LOGIN: 'Login',
    REGISTER: 'Register',
    HOME: 'Home',
    SEARCH: 'Search',
    NOTIFICATIONS: 'Notifications',
    MESSAGES: 'Messages',
    CHAT: 'Chat',
    REFERRALS: 'Referrals',
    REWARDS: 'Rewards',
    WALLET: 'Wallet',
    TRANSACTIONS: 'Transactions',
    REPORTS: 'Reports',
    ADMIN: 'Admin',
    MANAGEMENT: 'Management',
    CONFIGURATION: 'Configuration',
    SYSTEM: 'System',
    TOOLS: 'Tools',
    UTILITIES: 'Utilities',
  },
  
  // Dashboard
  DASHBOARD: {
    WELCOME_TITLE: 'Welcome to SwipeSage Admin',
    WELCOME_SUBTITLE: 'Manage your credit card database, track analytics, and monitor performance.',
    TOTAL_USERS: 'Total Users',
    TOTAL_CARDS: 'Total Cards',
    TOTAL_APPLICATIONS: 'Total Applications',
    PENDING_APPLICATIONS: 'Pending Applications',
    APPROVED_APPLICATIONS: 'Approved Applications',
    REJECTED_APPLICATIONS: 'Rejected Applications',
    RECENT_ACTIVITY: 'Recent Activity',
    QUICK_ACTIONS: 'Quick Actions',
    ADD_NEW_CARD: 'Add New Card',
    ADD_NEW_CARD_DESC: 'Create a new credit card entry',
    VIEW_ANALYTICS: 'View Analytics',
    VIEW_ANALYTICS_DESC: 'Check detailed analytics and reports',
    SEED_SAMPLE_DATA: 'Seed Sample Data',
    SEEDING_DATA: 'Seeding Data...',
    APPLICATION_STATUS: 'Application Status',
    QUICK_STATS: 'Quick Stats',
    NO_CARDS_FOUND: 'No cards found. Add your first card!',
    CONVERSION_RATE: 'Conversion Rate',
    CLICK_TO_APPLICATION: 'Click to application conversion',
    TOP_PERFORMING_CARD: 'Top Performing Card',
    MOST_CLICKED_REFERRAL: 'Most clicked referral link',
  },
  
  // Credit Cards
  CREDIT_CARDS: {
    TITLE: 'Credit Cards',
    SUBTITLE: 'Manage and view all credit cards in the system',
    ADD_NEW: 'Add New Credit Card',
    EDIT_CARD: 'Edit Credit Card',
    CARD_DETAILS: 'Card Details',
    BASIC_INFO: 'Basic Information',
    FINANCIAL_DETAILS: 'Financial Details',
    REWARDS_BENEFITS: 'Rewards & Benefits',
    FEES_CHARGES: 'Fees & Charges',
    APPLICATION_DETAILS: 'Application Details',
    ANALYTICS: 'Analytics',
    
    // Form Labels and Placeholders
    CARD_NAME: 'Card Name',
    CARD_NAME_PLACEHOLDER: 'Enter card name',
    BANK: 'Bank',
    BANK_PLACEHOLDER: 'Enter bank name',
    CATEGORY: 'Category',
    SELECT_CATEGORY: 'Select category',
    CARD_TYPE: 'Card Type',
    DESCRIPTION: 'Description',
    DESCRIPTION_PLACEHOLDER: 'Enter card description',
    ANNUAL_FEE: 'Annual Fee',
    JOINING_FEE: 'Joining Fee',
    INTEREST_RATE: 'Interest Rate',
    MIN_CREDIT_LIMIT: 'Minimum Credit Limit',
    MAX_CREDIT_LIMIT: 'Maximum Credit Limit',
    ACTIVE: 'Active',
    FEATURED: 'Featured',
    EDIT_CARD_DESC: 'Edit credit card details and settings',
    ADD_CARD_DESC: 'Add a new credit card to the system',
    
    // Search and Filters
    SEARCH_PLACEHOLDER: 'Search cards by name, bank, or category...',
    ALL_BANKS: 'All Banks',
    ALL_CATEGORIES: 'All Categories',
    ALL_MITC_STATUS: 'All MITC Status',
    WITH_MITC: 'With MITC Data',
    WITHOUT_MITC: 'Without MITC Data',
    
    // MITC Statistics
    MITC_STATS: 'MITC Integration Statistics',
    TOTAL_CARDS: 'Total Cards',
    
    // Fields
    NAME: 'Card Name',
    IMAGE_URL: 'Card Image URL',
    LOGO_URL: 'Bank Logo URL',
    CREDIT_LIMIT_MIN: 'Minimum Credit Limit',
    CREDIT_LIMIT_MAX: 'Maximum Credit Limit',
    CASHBACK_RATE: 'Cashback Rate',
    REWARD_POINTS: 'Reward Points',
    WELCOME_BONUS: 'Welcome Bonus',
    LATE_PAYMENT_FEE: 'Late Payment Fee',
    CASH_ADVANCE_FEE: 'Cash Advance Fee',
    FOREIGN_TRANSACTION_FEE: 'Foreign Transaction Fee',
    MIN_INCOME: 'Minimum Income',
    MIN_AGE: 'Minimum Age',
    MAX_AGE: 'Maximum Age',
    EMPLOYMENT_TYPE: 'Employment Type',
    MIN_CREDIT_SCORE: 'Minimum Credit Score',
    MAX_CREDIT_SCORE: 'Maximum Credit Score',
    REQUIRED_DOCUMENTS: 'Required Documents',
    PROCESSING_TIME: 'Processing Time',
    APPROVAL_RATE: 'Approval Rate',
    VIEWS: 'Views',
    APPLICATIONS: 'Applications',
    CONVERSIONS: 'Conversions',
    RATING: 'Rating',
    REVIEW_COUNT: 'Review Count',
    IS_ACTIVE: 'Active',
    IS_FEATURED: 'Featured',
    TAGS: 'Tags',
    
    // Empty States
    NO_CARDS_FOUND: 'No credit cards found',
    ADJUST_FILTERS: 'Try adjusting your search or filters',
    GET_STARTED: 'Get started by adding your first credit card',
    
    // Categories
    CATEGORIES: {
      PREMIUM: 'Premium',
      SHOPPING: 'Shopping',
      CASHBACK: 'Cashback',
      TRAVEL: 'Travel',
      FUEL: 'Fuel',
      STUDENT: 'Student',
      BUSINESS: 'Business',
      SECURED: 'Secured',
      PREPAID: 'Prepaid',
      DEBIT: 'Debit',
    },
    
    // Card Types
    CARD_TYPES: {
      CREDIT: 'Credit',
      DEBIT: 'Debit',
      PREPAID: 'Prepaid',
    },
    
    // Employment Types
    EMPLOYMENT_TYPES: {
      SALARIED: 'Salaried',
      SELF_EMPLOYED: 'Self Employed',
      STUDENT: 'Student',
      RETIRED: 'Retired',
      UNEMPLOYED: 'Unemployed',
    },
    
    // Actions
    ACTIONS: {
      VIEW_DETAILS: 'View Details',
      EDIT_CARD: 'Edit Card',
      DELETE_CARD: 'Delete Card',
      ACTIVATE_CARD: 'Activate Card',
      DEACTIVATE_CARD: 'Deactivate Card',
      FEATURE_CARD: 'Feature Card',
      UNFEATURE_CARD: 'Unfeature Card',
      DUPLICATE_CARD: 'Duplicate Card',
      EXPORT_CARD: 'Export Card',
    },
  },

  // MITC (Most Important Terms & Conditions)
  MITC: {
    TITLE: 'MITC Management',
    SUBTITLE: 'Upload and manage credit card MITC (Most Important Terms & Conditions) data',
    UPLOAD_HISTORY: 'MITC Upload History',
    UPLOAD_MODAL_TITLE: 'Upload MITC Data',
    UPLOAD_MODAL_DESCRIPTION: 'Upload a JSON file containing MITC data for credit cards',
    UPLOAD_INSTRUCTIONS: 'Drag and drop a JSON file here, or click to select file',
    UPLOAD_ACCEPTED_FORMATS: 'Only JSON files are accepted',
    UPLOAD_MAX_SIZE: 'Max file size: 10MB',
    
    // Table Headers
    TABLE_HEADERS: {
      FILE_NAME: 'File Name',
      UPLOAD_DATE: 'Upload Date',
      RECORDS: 'Records',
      FILE_SIZE: 'File Size',
      INTEGRATION_STATUS: 'Integration Status',
      ACTIONS: 'Actions',
    },
    
    // Status Messages
    STATUS: {
      PROCESSING: 'Processing...',
      NO_UPDATES_NEEDED: 'No updates needed',
      UPDATED: 'updated',
      UNMATCHED: 'unmatched',
      FAILED: 'Failed',
      AVAILABLE: 'Available',
      AWAITING: 'Awaiting',
    },
    
    // Integration Status
    INTEGRATION: {
      TITLE: 'Last Integration Status',
      TOTAL_CARDS: 'Total Cards',
      MATCHED_CARDS: 'Matched Cards',
      UPDATED_CARDS: 'Updated Cards',
      UNMATCHED_MITC: 'Unmatched MITC',
      MANUAL_INTEGRATION: 'Integrate with Cards',
      INTEGRATING: 'Integrating...',
      SEED_SAMPLE_CARDS: 'Seed Sample Cards',
      SEEDING: 'Seeding...',
      REFRESH: 'Refresh',
      REFRESHING: 'Refreshing...',
    },
    
    // Upload Messages
    UPLOAD_MESSAGES: {
      DUPLICATES_SKIPPED: '{count} duplicate records were skipped',
      NO_NEW_RECORDS: 'No new records were added - all records already exist',
      UPLOAD_COMPLETED: 'Upload completed - {count} duplicates skipped',
      SUCCESSFULLY_UPLOADED: 'Successfully uploaded {count} new MITC records from {fileName}',
      DUPLICATES_SKIPPED_SUFFIX: ' ({count} duplicates skipped)',
      PROCESSING_ERROR: 'Error processing file. Please check the JSON format.',
      INTEGRATION_SUCCESS: 'Successfully updated {count} existing credit cards with MITC data',
      INTEGRATION_SUCCESS_NEW_CARDS: 'Successfully created {count} new credit cards from MITC data',
      INTEGRATION_SUCCESS_MIXED: 'Successfully updated {updated} existing cards and created {created} new credit cards from MITC data',
      NO_CARDS_UPDATED: 'No credit cards were updated - data may already be current',
      UNMATCHED_RECORDS: '{count} MITC records couldn\'t be matched to existing cards',
      INTEGRATION_ERRORS: '{count} errors occurred during integration',
      INTEGRATION_FAILED: 'Failed to integrate MITC data with credit cards',
      NO_CARDS_UPDATED_MANUAL: 'No credit cards were updated',
      INTEGRATION_FAILED_MANUAL: 'Failed to integrate MITC data with credit cards',
      NO_MITC_RECORDS: 'No MITC records available for integration',
    },
    
    // Delete Messages
    DELETE_MESSAGES: {
      CONFIRM_DELETE_RECORD: 'Are you sure you want to delete this MITC record?',
      RECORD_DELETED: 'MITC record deleted successfully',
      FAILED_DELETE_RECORD: 'Failed to delete MITC record',
      CONFIRM_DELETE_SESSION: 'Delete Upload Session',
      DELETE_WARNING: '⚠️ This action cannot be undone!',
      DELETE_DESCRIPTION: 'You are about to delete the upload session "{fileName}" and all associated MITC records from the database.',
      DELETE_IMPACT: 'This will:',
      DELETE_IMPACT_1: '• Remove the upload history entry',
      DELETE_IMPACT_2: '• Delete all MITC records uploaded in this session',
      DELETE_IMPACT_3: '• Potentially affect credit cards that were updated with this data',
      DELETE_SESSION_BUTTON: 'Delete Session',
      DELETING: 'Deleting...',
      SESSION_DELETED: 'Successfully deleted upload session "{fileName}" and {count} associated MITC records',
      FAILED_DELETE_SESSION: 'Failed to delete upload session: {error}',
      CONFIRM_BULK_DELETE: 'Are you sure you want to delete {count} selected MITC record{plural}?',
      NO_RECORDS_SELECTED: 'No records selected for deletion',
      BULK_DELETE_SUCCESS: 'Successfully deleted {count} MITC record{plural}',
      BULK_DELETE_FAILED: 'Failed to delete some MITC records',
    },
    
    // Export Messages
    EXPORT_MESSAGES: {
      EXPORT_SUCCESS: 'MITC data exported successfully',
      EXPORT_FAILED: 'Failed to export MITC data',
      JSON_DOWNLOAD_SUCCESS: 'JSON file downloaded successfully',
      JSON_DOWNLOAD_FAILED: 'Failed to download JSON file',
      JSON_FILE_NOT_FOUND: 'JSON file not found',
    },
    
    // Sample Data Messages
    SAMPLE_MESSAGES: {
      CARDS_EXIST: '{count} credit cards already exist in the system',
      SAMPLE_ADDED: 'Successfully added {count} sample credit cards',
      FAILED_SEED: 'Failed to seed sample credit cards',
    },
    
    // Test Messages
    TEST_MESSAGES: {
      SESSION_DETAILS: 'Session has {count} associated records',
      SESSION_NOT_FOUND: 'Session not found',
      TEST_FAILED: 'Test failed',
    },
    
    // Search Messages
    SEARCH_MESSAGES: {
      SEARCH_FAILED: 'Failed to search MITC records',
    },
    
    // Loading Messages
    LOADING_MESSAGES: {
      LOAD_FAILED: 'Failed to load MITC data',
    },
    
    // Empty States
    EMPTY_STATES: {
      NO_UPLOADS: 'No MITC files uploaded yet',
      NO_UPLOADS_DESC: 'Upload your first MITC JSON file to see the history here',
      NO_CARDS_FOUND: 'No cards found. Add your first card!',
      ADJUST_FILTERS: 'Try adjusting your search or filters',
      GET_STARTED: 'Get started by adding your first credit card',
    },
    
    // Card Status
    CARD_STATUS: {
      MITC_DATA: 'MITC Data',
      MITC_STATUS: 'MITC Data Status',
      NO_MITC_DATA: 'No MITC (Most Important Terms & Conditions) data is currently available for this credit card.',
      MITC_AUTO_ADD: 'MITC data will be automatically added when a matching record is found during MITC file uploads.',
      AWAITING_MITC: 'Awaiting MITC Data',
      MITC_NOTE: 'Note: MITC data is automatically synchronized when you upload MITC JSON files in the MITC Management section. The system will match this card with MITC records based on card name and bank name similarity.',
    },
    
    // Buttons and Actions
    BUTTONS: {
      UPLOAD_MITC: 'Upload MITC',
      EXPORT_DATA: 'Export Data',
      CLEAR_FILTERS: 'Clear Filters',
      ALL_BANKS: 'All Banks',
      ALL_CATEGORIES: 'All Categories',
      SEARCH_PLACEHOLDER: 'Search MITC records...',
      NO_CARDS_FOUND: 'No cards found',
      ADJUST_FILTERS: 'Try adjusting your search or filters',
      GET_STARTED: 'Get started by adding your first credit card',
      BULK_DELETE: 'Delete Selected',
      SELECT_ALL: 'Select All',
      SELECTED_COUNT: '{count} record{plural} selected',
      DOWNLOAD_JSON: 'Download JSON',
      VIEW_JSON: 'View JSON',
    },
  },
  
  // Users
  USERS: {
    TITLE: 'Users',
    USER_DETAILS: 'User Details',
    PROFILE: 'Profile',
    PREFERENCES: 'Preferences',
    REFERRAL_INFO: 'Referral Information',
    ACTIVITY_HISTORY: 'Activity History',
    
    // Fields
    USER_ID: 'User ID',
    EMAIL: 'Email',
    DISPLAY_NAME: 'Display Name',
    PHONE_NUMBER: 'Phone Number',
    CREATED_AT: 'Created At',
    UPDATED_AT: 'Updated At',
    IS_ADMIN: 'Admin',
    IS_ACTIVE: 'Active',
    FIRST_NAME: 'First Name',
    LAST_NAME: 'Last Name',
    DATE_OF_BIRTH: 'Date of Birth',
    GENDER: 'Gender',
    OCCUPATION: 'Occupation',
    ANNUAL_INCOME: 'Annual Income',
    CREDIT_SCORE: 'Credit Score',
    SPENDING_CATEGORIES: 'Spending Categories',
    EXISTING_CARDS: 'Existing Cards',
    NOTIFICATIONS: 'Notifications',
    THEME: 'Theme',
    LANGUAGE: 'Language',
    REFERRAL_CODE: 'Referral Code',
    REFERRED_BY: 'Referred By',
    REFERRAL_COUNT: 'Referral Count',
    TOTAL_EARNINGS: 'Total Earnings',
    
    // Genders
    GENDERS: {
      MALE: 'Male',
      FEMALE: 'Female',
      OTHER: 'Other',
    },
    
    // Themes
    THEMES: {
      LIGHT: 'Light',
      DARK: 'Dark',
      SYSTEM: 'System',
    },
  },
  
  // Applications
  APPLICATIONS: {
    TITLE: 'Applications',
    APPLICATION_DETAILS: 'Application Details',
    PERSONAL_INFO: 'Personal Information',
    EMPLOYMENT_INFO: 'Employment Information',
    ADDRESS_INFO: 'Address Information',
    DOCUMENTS: 'Documents',
    
    // Fields
    APPLICATION_ID: 'Application ID',
    USER_ID: 'User ID',
    CARD_ID: 'Card ID',
    STATUS: 'Status',
    APPLIED_AT: 'Applied At',
    UPDATED_AT: 'Updated At',
    PROCESSED_AT: 'Processed At',
    APPROVED_AT: 'Approved At',
    REJECTED_AT: 'Rejected At',
    REJECTION_REASON: 'Rejection Reason',
    REFERRED_BY: 'Referred By',
    REFERRAL_CODE: 'Referral Code',
    
    // Statuses
    STATUSES: {
      PENDING: 'Pending',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      CANCELLED: 'Cancelled',
    },
  },
  
  // Analytics
  ANALYTICS: {
    TITLE: 'Analytics',
    OVERVIEW: 'Overview',
    PERFORMANCE: 'Performance',
    TRENDS: 'Trends',
    REPORTS: 'Reports',
    
    // Metrics
    TOTAL_CLICKS: 'Total Clicks',
    TOTAL_RECOMMENDATIONS: 'Total Recommendations',
    ACTIVE_USERS: 'Active Users',
    CONVERSION_RATE: 'Conversion Rate',
    AVERAGE_RATING: 'Average Rating',
    TOTAL_REVIEWS: 'Total Reviews',
    REVENUE: 'Revenue',
    PROFIT: 'Profit',
    COST: 'Cost',
    ROI: 'ROI',
    
    // Time Periods
    TODAY: 'Today',
    YESTERDAY: 'Yesterday',
    THIS_WEEK: 'This Week',
    LAST_WEEK: 'Last Week',
    THIS_MONTH: 'This Month',
    LAST_MONTH: 'Last Month',
    THIS_YEAR: 'This Year',
    LAST_YEAR: 'Last Year',
    CUSTOM: 'Custom',
  },
  
  // Settings
  SETTINGS: {
    TITLE: 'Settings',
    GENERAL: 'General',
    SECURITY: 'Security',
    NOTIFICATIONS: 'Notifications',
    PRIVACY: 'Privacy',
    APPEARANCE: 'Appearance',
    LANGUAGE: 'Language',
    CURRENCY: 'Currency',
    TIMEZONE: 'Timezone',
    DATE_FORMAT: 'Date Format',
    TIME_FORMAT: 'Time Format',
    NUMBER_FORMAT: 'Number Format',
    
    // General Settings
    SITE_NAME: 'Site Name',
    SITE_DESCRIPTION: 'Site Description',
    CONTACT_EMAIL: 'Contact Email',
    SUPPORT_EMAIL: 'Support Email',
    ADMIN_EMAIL: 'Admin Email',
    DEFAULT_LANGUAGE: 'Default Language',
    DEFAULT_CURRENCY: 'Default Currency',
    
    // Security Settings
    PASSWORD_POLICY: 'Password Policy',
    SESSION_TIMEOUT: 'Session Timeout',
    TWO_FACTOR_AUTH: 'Two-Factor Authentication',
    LOGIN_ATTEMPTS: 'Login Attempts',
    ACCOUNT_LOCKOUT: 'Account Lockout',
    
    // Notification Settings
    EMAIL_NOTIFICATIONS: 'Email Notifications',
    PUSH_NOTIFICATIONS: 'Push Notifications',
    SMS_NOTIFICATIONS: 'SMS Notifications',
    ADMIN_NOTIFICATIONS: 'Admin Notifications',
    USER_NOTIFICATIONS: 'User Notifications',
  },
  
  // Chat/AI
  CHAT: {
    TITLE: 'AI Chat',
    WELCOME_MESSAGE: 'Hello! I\'m your AI assistant. How can I help you find the perfect credit card?',
    PLACEHOLDER: 'Type your message here...',
    SEND: 'Send',
    TYPING: 'Typing...',
    ONLINE: 'Online',
    OFFLINE: 'Offline',
    BUSY: 'Busy',
    AWAY: 'Away',
    
    // AI Responses
    AI_RESPONSES: {
      GREETING: 'Hello! I\'m here to help you find the perfect credit card. What are you looking for?',
      INCOME_QUESTION: 'What is your annual income? This helps me recommend suitable cards.',
      SPENDING_QUESTION: 'What are your main spending categories? (e.g., shopping, dining, travel)',
      CREDIT_SCORE_QUESTION: 'Do you know your credit score? This affects card eligibility.',
      PREFERENCE_QUESTION: 'What\'s most important to you: rewards, low fees, or travel benefits?',
      RECOMMENDATION: 'Based on your profile, I recommend these cards:',
      NO_MATCHES: 'I couldn\'t find exact matches, but here are some general recommendations:',
      MORE_INFO: 'Would you like more information about any of these cards?',
      APPLY_NOW: 'Ready to apply? I can help you with the application process.',
      THANK_YOU: 'Thank you for using SwipeSage! Feel free to ask more questions.',
    },
  },
  
  // Referrals
  REFERRALS: {
    TITLE: 'Referrals',
    REFERRAL_PROGRAM: 'Referral Program',
    INVITE_FRIENDS: 'Invite Friends',
    EARN_REWARDS: 'Earn Rewards',
    REFERRAL_CODE: 'Referral Code',
    REFERRAL_LINK: 'Referral Link',
    SHARE_LINK: 'Share Link',
    COPY_LINK: 'Copy Link',
    SEND_INVITE: 'Send Invite',
    REFERRAL_HISTORY: 'Referral History',
    EARNINGS: 'Earnings',
    COMMISSION: 'Commission',
    PENDING: 'Pending',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
    
    // Messages
    INVITE_MESSAGE: 'Hey! I found this amazing credit card recommendation app called SwipeSage. Check it out!',
    SUCCESS_MESSAGE: 'Referral link copied to clipboard!',
    ERROR_MESSAGE: 'Failed to copy referral link. Please try again.',
  },
  
  // Notifications
  NOTIFICATIONS: {
    TITLE: 'Notifications',
    ALL: 'All',
    UNREAD: 'Unread',
    READ: 'Read',
    MARK_ALL_READ: 'Mark All as Read',
    CLEAR_ALL: 'Clear All',
    NO_NOTIFICATIONS: 'No notifications',
    
    // Types
    TYPES: {
      INFO: 'Information',
      SUCCESS: 'Success',
      WARNING: 'Warning',
      ERROR: 'Error',
    },
    
    // Messages
    APPLICATION_SUBMITTED: 'Your credit card application has been submitted successfully.',
    APPLICATION_APPROVED: 'Congratulations! Your credit card application has been approved.',
    APPLICATION_REJECTED: 'Your credit card application has been rejected.',
    NEW_CARD_AVAILABLE: 'A new credit card matching your profile is now available.',
    REFERRAL_EARNED: 'You earned ₹{amount} from your referral!',
    PAYMENT_DUE: 'Your credit card payment is due in {days} days.',
    WELCOME_BONUS: 'You\'ve earned your welcome bonus!',
    REWARD_POINTS: 'You\'ve earned {points} reward points.',
  },
  
  // Months and Days
  MONTHS: {
    JANUARY: 'January',
    FEBRUARY: 'February',
    MARCH: 'March',
    APRIL: 'April',
    MAY: 'May',
    JUNE: 'June',
    JULY: 'July',
    AUGUST: 'August',
    SEPTEMBER: 'September',
    OCTOBER: 'October',
    NOVEMBER: 'November',
    DECEMBER: 'December',
  },
  
  DAYS: {
    MONDAY: 'Monday',
    TUESDAY: 'Tuesday',
    WEDNESDAY: 'Wednesday',
    THURSDAY: 'Thursday',
    FRIDAY: 'Friday',
    SATURDAY: 'Saturday',
    SUNDAY: 'Sunday',
  },
  
  // Time
  TIME: {
    JUST_NOW: 'Just now',
    MINUTES_AGO: '{minutes} minutes ago',
    HOURS_AGO: '{hours} hours ago',
    DAYS_AGO: '{days} days ago',
    WEEKS_AGO: '{weeks} weeks ago',
    MONTHS_AGO: '{months} months ago',
    YEARS_AGO: '{years} years ago',
    TODAY: 'Today',
    YESTERDAY: 'Yesterday',
    TOMORROW: 'Tomorrow',
  },
  
  // Currency
  CURRENCY: {
    INR: '₹',
    USD: '$',
    EUR: '€',
    GBP: '£',
  },
  
  // Units
  UNITS: {
    PERCENTAGE: '%',
    PERCENT: '%',
    PERCENTAGE_SIGN: '%',
    CURRENCY: '₹',
    CURRENCY_SYMBOL: '₹',
    NUMBER: '#',
    COUNT: 'count',
    TIMES: 'times',
    DAYS: 'days',
    HOURS: 'hours',
    MINUTES: 'minutes',
    SECONDS: 'seconds',
    YEARS: 'years',
    MONTHS: 'months',
    WEEKS: 'weeks',
    POINTS: 'points',
    REWARDS: 'rewards',
    CASHBACK: 'cashback',
  },
} as const;

// Type for accessing nested string constants
export type AppStrings = typeof APP_STRINGS;
export type StringPath = keyof AppStrings;

// Helper function to get nested string values
export function getString(path: string): string {
  const keys = path.split('.');
  let value: any = APP_STRINGS;
  
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      console.warn(`String path not found: ${path}`);
      return path;
    }
  }
  
  return typeof value === 'string' ? value : path;
}

// Helper function to format strings with placeholders
export function formatString(template: string, values: Record<string, any>): string {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return values[key] !== undefined ? String(values[key]) : match;
  });
} 